import {
  type UrlDescType,
  type OrgUrl,
  getUrlsByOrganization,
  type BaseUrl,
  type PersonUrl,
  getUrlsByPerson
} from 'services'
import { UrlsBlock } from './urls-block'
import React, { useEffect, useState } from 'react'
import { isImageLogo } from 'helpers'
import PropTypes from 'prop-types'
import { URL_DESC_TYPES } from 'utils'
import { useUrlDescTypeLuts } from 'components'

export interface UrlsStackProps {
  orgId?: number | undefined
  personId?: number | undefined
}

export const UrlsStack: React.FC<UrlsStackProps> = ({ orgId = undefined, personId = undefined }) => {
  const [showLoading, setShowLoading] = useState(false)
  const { lutValues: urlDescTypeValues } = useUrlDescTypeLuts()
  const [urls, setUrls] = useState<BaseUrl[]>([])
  const [sources, setSources] = useState<BaseUrl[]>([])
  const [urlDescTypes, setUrlDescTypes] = useState<UrlDescType[]>([])
  const [reloadUrlComponent, setReloadUrlComponent] = useState<boolean>(false)
  const triggerReload = (): void => {
    setReloadUrlComponent(prevState => !prevState)
  }

  const filterUrlDescTypes = (urlDescTypes: UrlDescType[]): UrlDescType[] =>
    urlDescTypes?.filter(
      (urlDescType) =>
        urlDescType.id !== URL_DESC_TYPES.SOURCE &&
      urlDescType.id !== URL_DESC_TYPES.IMAGE_LOGO
    )

  useEffect(() => {
    const fetchUrls = async (): Promise<void> => {
      try {
        setShowLoading(true)
        if (orgId !== undefined) {
          const urlsResponse = await getUrlsByOrganization(orgId)
          setUrls(urlsResponse?.urls?.filter((url) => !isImageLogo(url)))
          setSources(urlsResponse?.sourceUrls)
        } else if (personId !== undefined) {
          const urlsResponse = await getUrlsByPerson(personId)
          setUrls(urlsResponse?.urls?.filter((url) => !isImageLogo(url)))
          setSources(urlsResponse.sourceUrls)
        } else {
          throw new Error('orgId and personId are both undefined')
        }
      } catch (error: any) {
        console.error(error)
      } finally {
        setUrlDescTypes(filterUrlDescTypes(urlDescTypeValues.descTypes))
        setShowLoading(false)
      }
    }
    void fetchUrls()
  }, [orgId, personId, reloadUrlComponent])

  const createNewUrlOrg = (): OrgUrl => {
    return {
      descId: 3,
      displayOrder: -1,
      id: 0,
      typeId: 2,
      value: '',
      orgId: orgId ?? 0,
      lastUpdated: new Date().toISOString()
    }
  }

  const createNewSourceOrg = (): OrgUrl => {
    const maxDisplayOrder = Math.max(...sources.map(source => source.displayOrder))
    return {
      descId: URL_DESC_TYPES.SOURCE,
      displayOrder: maxDisplayOrder + 1,
      id: 0,
      typeId: 2,
      value: '',
      orgId: orgId ?? 0,
      lastUpdated: new Date().toISOString()
    }
  }

  const createNewUrlPerson = (): PersonUrl => {
    return {
      descId: 3,
      displayOrder: -1,
      id: 0,
      typeId: 2,
      value: '',
      personId: personId ?? 0,
      lastUpdated: new Date().toISOString()
    }
  }

  const createNewSourcePerson = (): PersonUrl => {
    return {
      descId: URL_DESC_TYPES.SOURCE,
      displayOrder: sources.length + 1,
      id: -1,
      typeId: 2,
      value: '',
      personId: personId ?? 0,
      lastUpdated: new Date().toISOString()
    }
  }

  return (
    <React.Fragment>
      <UrlsBlock
        originalUrls={urls}
        urlDescTypes={urlDescTypes}
        loading={showLoading}
        createNewUrl={orgId !== undefined ? createNewUrlOrg : createNewUrlPerson}
        title={'URL'}
        disableDrag={true}
        triggerReload={triggerReload} />

      <UrlsBlock
        originalUrls={sources}
        urlDescTypes={[]}
        loading={showLoading}
        createNewUrl={orgId !== undefined ? createNewSourceOrg : createNewSourcePerson}
        title={'Source'}
        triggerReload={triggerReload} />
    </React.Fragment>
  )
}

UrlsStack.propTypes = {
  orgId: PropTypes.number,
  personId: PropTypes.number
}
